import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardComponent } from '../../molecules/card/card.component';
import {
  ContactCard,
  TextBtnCard,
} from '../../../shared/interfaces/card.interface';
import LinkType from '../../../shared/enums/link-type.enums';
import {
  Link,
  socialMediaLinks,
} from '../../../shared/interfaces/link.interface';
import { LinkComponent } from '../../atoms/link/link.component';
import CardType from '../../../shared/enums/card-type.eums';
import { IconComponent } from '../../atoms/icon/icon.component';
import { Sizes, Colors } from '../../../shared/enums/shared.enums';
import { BubbleComponent } from '../../atoms/bubble/bubble.component';
import {
  CaasFooter,
  CaasFormField,
  CaasInternalLink,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';

@Component({
  selector: 'hdi-footer',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    LinkComponent,
    IconComponent,
    BubbleComponent,
    CapitalizePipe,
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent implements OnChanges {
  @Input() caasData?: CaasFooter;
  @Input() headline1? = '';
  @Input() headline2? = '';
  @Input() headline3? = '';
  @Input() headline4? = '';
  @Input() cards: ContactCard[] = [];
  @Input() links: Link[] = [];
  @Input() textBtnCards: TextBtnCard[] = [];
  @Input() socialLinks: socialMediaLinks[] = [];
  @Input() legalLinks: Link[] = [];

  // Enums and constants
  CardType = CardType;
  HrefType = HrefType;
  LinkType = LinkType;
  Sizes = Sizes;
  Colors = Colors;
  TextColors = Colors;

  constructor(
    private cdr: ChangeDetectorRef,
    private caasRoutesService: CaasRoutesService,
    private router: Router,
    private caasBodytextService: CaasBodytextService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['caasData']?.currentValue &&
      this.caasData &&
      Object.keys(this.caasData || {}).length
    ) {
      this.setCaasContent();
      this.cdr.detectChanges();
    }
  }

  // Method to handle setting content for footer
  private setCaasContent(): void {
    if (!this.caasData) return;

    // Contact Cards setup
    this.setContactCards();

    // Link configurations for company, help, newsletter, and social media links
    const linkConfigurations = [
      { key: 'gc_company_links', method: this.setLinksList.bind(this) },
      { key: 'gc_help_link', method: this.setHelpLinks.bind(this) },
      { key: 'gc_newsletter_link', method: this.setNewsletterLinks.bind(this) },
      { key: 'gc_social_media_list', method: this.setSocialLinks.bind(this) },
    ];

    // Dynamically setting the content for each link type if data exists
    linkConfigurations.forEach(({ key, method }) => {
      if (this.caasData && this.caasData[key]?.value) {
        method();
      } else {
        console.warn(
          `FooterComponent > linkConfigurations > No data found for: ${key}`
        );
      }
    });

    // Setting legal links (Impressum and Datenschutz)
    this.setImprintLinks();
    this.setLegacyPolicyLinks();
  }

  // contact cards
  private setContactCards(): void {
    this.addPhoneContactCard();
    this.addContactFormCard(
      'envelope',
      'gc_contact_form_headline',
      'gc_contact_form_internal_link',
      'gc_contact_form_description'
    );
    this.addContactFormCard(
      'message-exclamation',
      'gc_contact_form_schaden_headline',
      'gc_contact_form_schaden_internal_link',
      'gc_contact_form_schaden_description'
    );
  }
  getTelephoneLink(phone: string): string {
    return phone.match(/^\+?[0-9\s\-().]*$/) ? `tel:${phone}` : '';
  }

  // Add phone contact card
  private addPhoneContactCard(): void {
    const phoneHeader = this.caasData?.gc_telephone_header?.value;
    if (phoneHeader && !this.cards.some((card) => card.icon === 'phone')) {
      const phoneCard: ContactCard = {
        icon: 'phone',
        title: phoneHeader,
        textLink:
          this.caasData?.gc_telephone_link?.value?.formData?.lt_text?.value?.toString() ||
          '',
        link: this.getTelephoneLink(
          this.caasData?.gc_telephone_link?.value?.formData?.lt_telephone?.value?.toString() ||
            ''
        ),
        linkType: LinkType.INLINE_LIGHT,
        date: this.caasData?.gc_telephone_subheader?.value?.toString() || '',
        disabled: false,
        iconColor: Colors.BLACK,
        linkTarget: '_self',
      };
      this.cards.push(phoneCard);
    }
  }

  // Add a general contact form card
  private addContactFormCard(
    icon: string,
    headlineKey: string,
    linkKey: string,
    descriptionKey: string
  ): void {
    if (
      this.caasData?.[headlineKey]?.value &&
      !this.cards.some((card) => card.icon === icon)
    ) {
      const newCard: ContactCard = {
        icon,
        title: this.caasData[headlineKey]?.value?.toString() || '',
        textLink:
          this.caasData[
            linkKey
          ]?.value?.formData?.lt_link_label?.value?.toString() || '',
        link:
          this.caasRoutesService.getSeoRouteByIdentifier(
            this.caasData[linkKey]?.value?.formData?.lt_link_internal?.value
              ?.identifier ?? '',
            this.router.config
          )?.path ?? '',
        subtitle: this.caasData[descriptionKey]?.value?.toString() || '',
        disabled: false,
        iconColor: Colors.BLACK,
        linkType: LinkType.PRIMARY_ARROW,
        linkTarget: '_self',
      };
      this.cards.push(newCard);
    }
  }

  // Process the link data and return formatted Link object
  private processLinkData(linkData: {
    lt_link_label: CaasSimpleField;
    lt_link_external: CaasSimpleField;
    lt_link_internal: CaasInternalLink;
    lt_link_title: CaasSimpleField;
    lt_link_type: CaasFormField;
    lt_link_vimeo_id: CaasSimpleField;
    st_anchor_name: CaasSimpleField;
  }): Link {
    const isExternal = linkData?.lt_link_type?.value?.identifier === 'external';

    const linkType = isExternal ? LinkType.EXTERNAL : LinkType.PRIMARY_ARROW;
    const href = isExternal
      ? (linkData?.lt_link_external?.value?.toString() ?? '')
      : (this.caasRoutesService.getSeoRouteByIdentifier(
          linkData?.lt_link_internal?.value?.identifier ?? '',
          this.router.config
        )?.path ?? '404');
    return {
      label: linkData?.lt_link_label?.value ?? '',
      href,
      hrefType: isExternal ? HrefType.EXTERNAL : HrefType.ANGULAR_ROUTE,
      target: isExternal ? '_blank' : '_self',
      linkType,
    };
  }

  // Set links for the company section
  setLinksList(): void {
    this.headline2 =
      this.caasData?.gc_company_header?.value || 'Default Headline';
    const companyLinks = this.caasData?.gc_company_links?.value;
    this.links = companyLinks
      ? companyLinks.map((link) => this.processLinkData(link?.formData))
      : [];
  }

  // Set imprint and privacy links
  private setLegalLinks(key: string, fallbackLabel: string): Link[] {
    const data = this.caasData?.[key]?.value?.formData;
    if (!data) {
      console.warn(
        `FooterComponent > setLegalLinks: No valid ${key} data found.`
      );
      return [{ label: fallbackLabel, href: '' }];
    }
    return [
      {
        label: data.lt_link_label?.value || fallbackLabel,
        href:
          this.caasRoutesService.getSeoRouteByIdentifier(
            data.lt_link_internal?.value?.identifier ?? '',
            this.router.config
          )?.path ?? '404',
      },
    ];
  }

  setImprintLinks(): void {
    this.legalLinks = this.setLegalLinks('gc_imprint', 'Impressum');
  }

  setLegacyPolicyLinks(): void {
    this.legalLinks = [
      ...this.legalLinks,
      ...this.setLegalLinks('gc_privacy', 'Datenschutz'),
    ];
  }

  // Set help links and text button cards
  setHelpLinks(): void {
    const helpData = this.caasData?.gc_help_link?.value?.formData ?? null;

    if (!helpData) {
      console.warn('No help data found.');
      return;
    }

    this.headline3 = this.caasData?.gc_help_headline?.value?.toString() ?? '';
    const hrefType = this.caasData?.gc_help_link
      ? this.caasBodytextService.getHrefType(this.caasData?.gc_help_link)
      : HrefType.ANGULAR_ROUTE;

    this.textBtnCards.push({
      subtitle: this.caasData?.gc_help_description?.value?.toString() ?? '',
      btnLabel: helpData?.lt_link_label?.value?.toString() ?? '',
      btnHrefType: hrefType,
      btnHref: this.caasData?.gc_help_link
        ? this.caasBodytextService.getHref(this.caasData?.gc_help_link)
        : '',
      linkTitle: helpData.lt_link_title.value ?? '',
      linkRel: helpData.lt_link_follow.value.identifier,
      btnHeadline: this.caasData?.gc_help_cta_headline?.value?.toString() ?? '',
    });
  }

  // Set newsletter links and text button cards
  setNewsletterLinks(): void {
    this.headline4 =
      this.caasData?.gc_newsletter_headline?.value?.toString() ?? '';
    const newsletterData =
      this.caasData?.gc_newsletter_link?.value?.formData ?? null;
    const hrefType = this.caasData?.gc_newsletter_link
      ? this.caasBodytextService.getHrefType(this.caasData?.gc_newsletter_link)
      : HrefType.ANGULAR_ROUTE;

    if (newsletterData) {
      this.textBtnCards.push({
        btnHeadline:
          this.caasData?.gc_newsletter_cta_headline?.value?.toString() ?? '',
        subtitle:
          this.caasData?.gc_newsletter_description?.value?.toString() ?? '',
        btnLabel: newsletterData?.lt_link_label?.value?.toString() ?? '',
        btnHrefType: hrefType,
        btnHref: this.caasData?.gc_newsletter_link
          ? this.caasBodytextService.getHref(this.caasData?.gc_newsletter_link)
          : '',
        btnTarget: '_blank',
        linkTitle:
          this.caasData?.gc_newsletter_link?.value?.formData?.lt_link_title
            .value ?? '',
        linkRel:
          this.caasData?.gc_newsletter_link?.value?.formData?.lt_link_follow
            .value.identifier,
      });
    }
  }

  setSocialLinks() {
    const socialMediaList = this.caasData?.gc_social_media_list?.value;

    if (!Array.isArray(socialMediaList) || !socialMediaList.length) {
      console.warn('FooterComponent -> No valid social media links found.');
      this.socialLinks = [];
      return;
    }

    this.socialLinks = socialMediaList.map((link) => {
      const identifier = link.formData?.lt_icontype?.value?.identifier || '';
      let iconPrefix = 'fab';
      let icon;

      switch (identifier) {
        case 'facebook':
          icon = 'facebook-f';
          break;
        case 'linkedin':
          icon = 'linkedin-in';
          break;
        case 'custom':
          icon = 'globe';
          iconPrefix = 'fal';
          break;
        default:
          icon = identifier;
      }

      return {
        icon,
        href: link.formData?.lt_link_external?.value || '',
        iconPrefix,
      };
    });
  }

  get hasInvalidLegalLinks(): boolean {
    return (
      Array.isArray(this.legalLinks) &&
      this.legalLinks.some((a) => a?.href === '')
    );
  }
}
